<template>
  <div class="club-info">
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="5" animated />
    </div>
    <div v-else-if="!clubData" class="empty-container">
      <el-empty :description="$t('settings.noClubInfo')" />
    </div>
    <div v-else class="club-display">
      <div class="club-header" v-if="clubData.logoImageUrl">
        <img :src="clubData.logoImageUrl" alt="Club Logo" class="club-logo" />
      </div>
      
      <div class="info-grid">
        <div class="info-item">
          <div class="info-label">{{ $t('settings.clubName') }}</div>
          <div class="info-value">{{ clubData.name || '-' }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.customerCompanyName">
          <div class="info-label">{{ $t('settings.companyName') }}</div>
          <div class="info-value">{{ clubData.customerCompanyName }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.address">
          <div class="info-label">{{ $t('settings.address') }}</div>
          <div class="info-value">
            <a 
              v-if="clubData.latitude !== null && clubData.longitude !== undefined" 
              :href="getMapUrl(clubData.latitude, clubData.longitude)" 
              target="_blank" 
              class="address-link"
            >
              {{ clubData.address }}
              <el-icon class="map-icon"><Location /></el-icon>
            </a>
            <span v-else>{{ clubData.address }}</span>
          </div>
        </div>
        
        <div class="info-item" v-if="clubData.servicePhone">
          <div class="info-label">{{ $t('settings.servicePhone') }}</div>
          <div class="info-value">
            <a :href="`tel:${clubData.servicePhone}`" class="phone-link">
              {{ clubData.servicePhone }}
              <el-icon class="phone-icon"><Phone /></el-icon>
            </a>
          </div>
        </div>
        
        <div class="info-item" v-if="clubData.description">
          <div class="info-label">{{ $t('settings.description') }}</div>
          <div class="info-value">{{ clubData.description }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.appId">
          <div class="info-label">{{ $t('settings.appId') }}</div>
          <div class="info-value">{{ clubData.appId }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.subMchId">
          <div class="info-label">{{ $t('settings.subMchId') }}</div>
          <div class="info-value">{{ clubData.subMchId }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.operationMode !== null && clubData.operationMode !== undefined">
          <div class="info-label">{{ $t('settings.operationMode') }}</div>
          <div class="info-value">
            {{ getOperationModeText(clubData.operationMode) }}
            <span v-if="clubData.yearlyFee !== null && clubData.yearlyFee !== undefined && clubData.operationMode === 1" class="fee-info">
              ({{ $t('settings.yearlyFee') }}: ¥{{ clubData.yearlyFee.toFixed(2) }})
            </span>
          </div>
        </div>
        
        <div class="info-item" v-if="clubData.commissionRate !== null && clubData.commissionRate !== undefined && clubData.commissionRate !== 0">
          <div class="info-label">{{ $t('settings.commissionRate') }}</div>
          <div class="info-value">{{ clubData.commissionRate }}%</div>
        </div>
        
        <div class="info-item" v-if="clubData.platformFeeExpiryDate">
          <div class="info-label">{{ $t('settings.platformFeeExpiryDate') }}</div>
          <div class="info-value">{{ formatDate(clubData.platformFeeExpiryDate) }}</div>
        </div>
        
        <div class="info-item" v-if="clubData.email">
          <div class="info-label">{{ $t('settings.email') }}</div>
          <div class="info-value">
            <a :href="`mailto:${clubData.email}`" class="email-link">
              {{ clubData.email }}
              <el-icon class="email-icon"><Message /></el-icon>
            </a>
          </div>
        </div>
        
        <div class="info-item" v-if="clubData.updatedAt">
          <div class="info-label">{{ $t('settings.updatedAt') }}</div>
          <div class="info-value">{{ formatDateTime(clubData.updatedAt) }}</div>
        </div>
        
        <div class="info-item">
          <div class="info-label">{{ $t('settings.status') }}</div>
          <div class="info-value">
            <el-tag :type="clubData.isActive ? 'success' : 'danger'" size="small">
              {{ clubData.isActive ? $t('common.yes') : $t('common.no') }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Location, Phone, Message } from '@element-plus/icons-vue'
import { getClubInfo } from '@/api/club'

const { t } = useI18n()
const loading = ref(true)
const clubData = ref(null)

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const getOperationModeText = (mode) => {
  const modes = {
    0: t('settings.operationModeCommission'),
    1: t('settings.operationModeYearlyFee')
  }
  return modes[mode] || `-`
}

const getMapUrl = (latitude, longitude) => {
  // Use Chinese map services for better compatibility in China
  // Priority: Baidu Maps, then Amap (Gaode)
  
  // Primary: Baidu Maps (most popular in China)
  return `https://api.map.baidu.com/marker?location=${latitude},${longitude}&title=Location&content=&output=html`
}

const fetchClub = async () => {
  loading.value = true
  try {
    const res = await getClubInfo()
    if (res.success && res.data) {
      const club = Array.isArray(res.data) ? res.data[0] : res.data
      clubData.value = club
    }
  } catch (error) {
    console.error('Failed to fetch club:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchClub()
})
</script>

<style scoped lang="scss">
.club-info {
  .loading-container {
    padding: 20px;
    
    @media (max-width: 768px) {
      padding: 16px;
    }
  }
  
  .empty-container {
    padding: 40px 0;
    
    @media (max-width: 768px) {
      padding: 30px 0;
    }
  }
  
  .club-display {
    .club-header {
      display: flex;
      justify-content: center;
      margin-bottom: 24px;
      
      @media (max-width: 768px) {
        margin-bottom: 20px;
      }
      
      .club-logo {
        width: 100px;
        height: 100px;
        border-radius: 12px;
        object-fit: contain;
        border: 1px solid var(--border-color-light, #e4e7ed);
        padding: 8px;
        background: var(--bg-color-light, #f5f7fa);
        transition: all 0.3s ease;
        
        @media (max-width: 768px) {
          width: 80px;
          height: 80px;
        }
        
        html.dark & {
          background: var(--bg-color-white, #2d3748);
          border-color: var(--border-color-light, #4a5568);
        }
      }
    }
    
    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      
      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 12px;
      }
      
      .info-item {
        background: var(--bg-color-light, #fafafa);
        border-radius: 8px;
        padding: 16px;
        transition: background-color 0.3s ease;
        
        @media (max-width: 768px) {
          padding: 14px;
        }
        
        html.dark & {
          background: var(--bg-color-base, #2d3748);
          border: 1px solid var(--border-color-light, #4a5568);
        }
        
        .info-label {
          color: var(--color-text-secondary, #909399);
          font-size: 13px;
          margin-bottom: 6px;
          
          @media (max-width: 768px) {
            font-size: 12px;
            margin-bottom: 4px;
          }
        }
        
        .info-value {
          color: var(--color-text-primary, #303133);
          font-weight: 500;
          word-break: break-all;
          font-size: 14px;
          
          @media (max-width: 768px) {
            font-size: 13px;
          }
          
          .website-link {
            color: var(--el-color-primary, #409eff);
            text-decoration: none;
            
            &:hover { text-decoration: underline; }
          }
          
          .fee-info {
            display: inline-block;
            margin-left: 8px;
            color: var(--el-color-primary, #409eff);
            font-weight: 600;
            font-size: 13px;
            
            @media (max-width: 768px) {
              display: block;
              margin-left: 0;
              margin-top: 4px;
            }
          }
          
          .address-link,
          .phone-link,
          .email-link {
            color: var(--el-color-primary, #409eff);
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.3s ease;
            
            &:hover {
              text-decoration: underline;
              opacity: 0.8;
            }
            
            .map-icon,
            .phone-icon,
            .email-icon {
              font-size: 16px;
            }
          }
        }
      }
    }
  }
}
</style>
